import {Box, FAQ, FAQGroup, Grid, Section, Text} from '@primer/react-brand/esm'
import {codeMarkRenderer} from '@github-ui/swp-core/components/contentful/shared/RichTextFieldRenderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {renderWithoutParagraphs, renderWithSpans} from '../../../utils'
import type {OutroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'
import styles from './OutroSection.module.css'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroCustomerStories} from '../../../components/FlexSuiteOutroCustomerStories'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {clsx} from 'clsx'
import {
  FlexSuiteTestimonialBanner,
  FlexSuiteTestimonialQuote,
  FlexSuiteTestimonialStat,
} from '../../../components/FlexSuiteTestimonial'
import {OutroStats} from './variants'
import {useBrandedDivider} from '../../../hooks/useBrandedDivider'

export const OutroSection = ({
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  includeFaqs,
  faqHeading,
  outroTestimonial,
  outroTestimonialLink,
  outroTestimonialLogo,
  outroTestimonialQuote,
  outroTestimonialImage,
  outroTestimonialLabel,
  outroTestimonialStats,
  outroTestimonialName,
  outroTestimonialTitle,
}: OutroSectionProps) => {
  const validFaqGroups = includeFaqs
    ? outroFaqs?.filter(group => group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer))
    : undefined
  const singleFaqGroup = validFaqGroups && validFaqGroups.length === 1 ? validFaqGroups[0] : null

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const hasStats = outroTestimonial === 'Stats' && Boolean(outroTestimonialStats?.length)
  const hasFullStatsTestimonial =
    hasStats && Boolean(outroTestimonialQuote && outroTestimonialLink && outroTestimonialImage)
  const hasTestimonial =
    (outroTestimonial === 'Quote' && Boolean(outroTestimonialQuote)) ||
    hasStats ||
    (outroTestimonial === 'Brand Banner' && Boolean(outroTestimonialQuote))

  const {hasBrandedDivider, BrandedDivider} = useBrandedDivider()

  if (
    !hasTestimonial &&
    !hasCustomerStories &&
    !outroCtaBanner &&
    (!outroIncludeResources || !outroResources?.length) &&
    (!validFaqGroups || validFaqGroups.length === 0)
  ) {
    return null
  }

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section paddingBlockStart="none" className={baseStyles.gridFrameTop} paddingBlockEnd="none" fullWidth>
        {hasCustomerStories && <FlexSuiteOutroCustomerStories variant="icon-cards" stories={outroCustomerStories} />}

        {outroTestimonial === 'Quote' && outroTestimonialQuote ? (
          <FlexSuiteTestimonialQuote
            quote={outroTestimonialQuote}
            name={outroTestimonialName}
            title={outroTestimonialTitle}
            link={outroTestimonialLink && outroTestimonialLink.fields}
            logo={outroTestimonialLogo}
          />
        ) : null}

        {hasFullStatsTestimonial && outroTestimonialQuote && outroTestimonialLink && outroTestimonialImage ? (
          <Box marginBlockStart={48}>
            <FlexSuiteTestimonialStat
              quote={outroTestimonialQuote}
              link={outroTestimonialLink.fields}
              label={outroTestimonialLabel}
              image={outroTestimonialImage}
              stats={outroTestimonialStats}
            />
          </Box>
        ) : hasStats ? (
          <Box marginBlockStart={48}>
            <OutroStats stats={outroTestimonialStats} />
          </Box>
        ) : null}

        {outroTestimonial === 'Brand Banner' && outroTestimonialQuote ? (
          <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
            <FlexSuiteTestimonialBanner
              quote={outroTestimonialQuote}
              link={outroTestimonialLink && outroTestimonialLink.fields}
              logo={outroTestimonialLogo}
              image={outroTestimonialImage}
            />
          </Box>
        ) : null}

        {outroCtaBanner && (
          <Box
            className={clsx(baseStyles.gridFrameBottom, hasTestimonial && baseStyles.gridFrameTop)}
            marginBlockStart={hasTestimonial ? {narrow: 'spacious', regular: 64} : 'none'}
          >
            <FlexSuiteOutroCTA
              outroCtaHeading={outroCtaHeading}
              outroCtaDescription={outroCtaDescription}
              outroCtaOne={outroCtaOne}
              outroCtaTwo={outroCtaTwo}
            />
          </Box>
        )}

        {outroIncludeResources && (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        )}

        {hasBrandedDivider ? (
          <Box className={baseStyles.gridFrameBottom}>
            <Grid>
              <Grid.Column>
                <BrandedDivider />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {validFaqGroups && validFaqGroups.length > 1 && (
          <Box paddingBlockStart={faqHeading ? undefined : {narrow: 'spacious', regular: 64}}>
            <FAQGroup variant="gridline" defaultSelectedIndex={0}>
              {faqHeading && (
                <FAQGroup.Heading>
                  <Box
                    paddingBlockStart={{narrow: 'spacious', regular: 64}}
                    paddingInlineStart={{narrow: 'none', regular: 'spacious'}}
                  >
                    {renderWithSpans(faqHeading, 'd-block')}
                  </Box>
                </FAQGroup.Heading>
              )}

              {validFaqGroups.map(group => {
                const completeFaqs = group.fields.faqs.filter(faq => faq.fields.question && faq.fields.answer)

                return (
                  <FAQ key={group.sys.id} variant="gridline">
                    {group.fields.heading && <FAQ.Heading>{renderWithoutParagraphs(group.fields.heading)}</FAQ.Heading>}
                    {completeFaqs.map((faq, index) => (
                      <FAQ.Item key={faq.sys.id} name={`faq-${group.sys.id}`} open={index === 0}>
                        <FAQ.Question>{faq.fields.question}</FAQ.Question>
                        <FAQ.Answer>
                          <Text as="div" variant="muted">
                            {documentToReactComponents(faq.fields.answer, {renderMark: codeMarkRenderer})}
                          </Text>
                        </FAQ.Answer>
                      </FAQ.Item>
                    ))}
                  </FAQ>
                )
              })}
            </FAQGroup>
          </Box>
        )}

        {singleFaqGroup && (
          <Box
            className={faqHeading ? undefined : styles.StandaloneFaq}
            paddingBlockStart={faqHeading ? undefined : {narrow: 'spacious', regular: 64}}
          >
            <FAQ key={singleFaqGroup.sys.id} variant="gridline">
              {faqHeading && (
                <FAQ.Heading>
                  <Box
                    paddingBlockStart={{narrow: 'spacious', regular: 64}}
                    paddingInlineStart={{narrow: 'none', regular: 'spacious'}}
                  >
                    {renderWithSpans(faqHeading, 'd-block')}
                  </Box>
                </FAQ.Heading>
              )}
              {!faqHeading && singleFaqGroup.fields.heading && (
                <FAQ.Heading>{renderWithoutParagraphs(singleFaqGroup.fields.heading)}</FAQ.Heading>
              )}
              {singleFaqGroup.fields.faqs
                .filter(faq => faq.fields.question && faq.fields.answer)
                .map((faq, index) => (
                  <FAQ.Item key={faq.sys.id} name={`faq-${singleFaqGroup.sys.id}`} open={index === 0}>
                    <FAQ.Question>{faq.fields.question}</FAQ.Question>
                    <FAQ.Answer>
                      <Text as="div" variant="muted">
                        {documentToReactComponents(faq.fields.answer, {renderMark: codeMarkRenderer})}
                      </Text>
                    </FAQ.Answer>
                  </FAQ.Item>
                ))}
            </FAQ>
          </Box>
        )}
      </Section>
    </Box>
  )
}
